import Core, { symmetricMod } from 'smooothy'
import gsap from 'gsap'
import { splitCaption, linesOut, linesIn, extrasOut, extrasIn, hideExtras } from '../utils/captionLines'
import { bindPointerLook } from '../utils/pointerLook'

/**
 * DOM side of the record slider. Smooothy handles the swipe physics on the
 * invisible slides; RecordScene reads `offsetOf()` each frame to place the meshes.
 *
 * Markup:
 *   <div data-record-slider>                       ← wrapper (this)
 *     <div data-record-slide="slug" data-title data-subtitle>
 *       <a href="…"></a>                           ← hit area, sized to the sleeve
 *     </div>
 *   </div>
 *   <div data-record-caption> … </div>             ← sibling, optional
 */
export default class RecordSlider extends Core {
	#shown = null // index whose text is in the DOM (or on its way in)
	#pending = null // latest index requested by the slider
	#phase = 'idle' // 'idle' | 'out' | 'wait' (out, debouncing the swipe) | 'in' | 'held' (hidden, waiting for the page transition)
	#split = null // live SplitText; stays in place at rest (reverting reflows the text)
	#tween = null
	#enterDelay = null // debounce between the lines going out and the new text coming in
	#listeners = new AbortController() // window listeners, dropped on destroy()
	#leaving = false // page transition has taken the caption out; don't bring anything back

	/**
	 * @param {HTMLElement} wrapper
	 * @param {{ onHover?: (slug: string, on: boolean, look?: { x: number, y: number }) => void, onPress?: (slug: string, on: boolean) => void }} [callbacks]
	 */
	constructor(wrapper, { onHover, onPress } = {}) {
		super(wrapper, {
			infinite: true,
			snap: true,
			lerpFactor: 0.25,
			onSlideChange: index => this.#renderCaption(index),
		})

		this.dragThreshold = 6 // px of pointer travel before a click counts as a drag
		this.dragRatio = 2.25 // slides moved per one slide-width of pointer travel, whatever the screen size
		this.flickTime = 0.12 // s of release velocity projected forward when deciding where to snap
		this.flickMin = 0.35 // px/ms - a release faster than this always moves at least one slide
		this.flickMax = 2 // slides a single flick can carry past where the finger let go
		this.captionSettle = 0.15 // s without a slide change before the caption text comes in (a fast swipe passes several)
		this.onResize = () => this.#tuneDrag()
		this.#tuneDrag()
		this.ringNudge = 0.02 // em, optical correction so the ring centres on the glyph rather than its box
		this.slugs = this.items.map(slide => slide.dataset.recordSlide)
		this.caption = wrapper.parentElement.querySelector('[data-record-caption]')
		this.ring = this.caption?.querySelector('[data-caption-ring]')
		this.dots = [...(this.caption?.querySelectorAll('[data-caption-index]') ?? [])]

		this.#bindClicks()
		this.#bindPointer(onHover, onPress)
		this.#setCaption(this.currentSlide)
		// the index is em-sized, so the ring's target moves once webfonts land
		document.fonts?.ready.then(() => this.#placeRing(this.#shown, true))
	}

	destroy() {
		this.#listeners.abort()
		this.#tween?.kill()
		this.#enterDelay?.kill()
		this.#split?.revert()
		super.destroy()
	}

	/** Distance between slide centres, px. */
	get pitch() {
		return this.viewport.itemWidth
	}

	/** Rendered sleeve size, px (the hit-area link inside the slide). */
	get slideSize() {
		return this.items[0].firstElementChild.offsetWidth
	}

	/** Vertical centre of the slider in viewport px. */
	get centerY() {
		const { top, height } = this.wrapper.getBoundingClientRect()
		return top + height / 2
	}

	/** Position of a slide in slide widths from the centre (0 = active). */
	offsetOf(slug) {
		const index = this.slugs.indexOf(slug)
		return this.parallaxValues?.[index] ?? symmetricMod(this.current + index, this.items.length)
	}

	/** Centre a slide instantly (used when returning from its page). */
	jumpTo(slug) {
		const index = this.slugs.indexOf(slug)
		if (index < 0) return
		this.current = this.target = -index
		this.#setCaption(index) // otherwise next frame's onSlideChange would animate it in
	}

	/** Scroll the shortest way round to a slide. */
	goTo(index) {
		this.target = Math.round(this.current - this.offsetOf(this.slugs[index]))
	}

	/**
	 * If `url` is one of the slides' pages, scroll to that slide and resolve once
	 * the slider has settled on it — so a header link to a record leaves from the
	 * same centred position a slide click would. Resolves immediately otherwise.
	 */
	settleOn(url) {
		const pathname = new URL(url, location.href).pathname
		const index = this.items.findIndex(slide => {
			const link = slide.querySelector('a[href]')
			return link && new URL(link.href).pathname === pathname
		})
		if (index < 0) return Promise.resolve()

		this.goTo(index)
		return new Promise(resolve => {
			const deadline = performance.now() + 2000 // never hold the navigation hostage
			const check = () => {
				const settled = Math.abs(this.current - this.target) < 0.002
				settled || performance.now() > deadline ? resolve() : requestAnimationFrame(check)
			}
			requestAnimationFrame(check)
		})
	}

	// Smooothy's dragSensitivity is slides-per-px, so the same thumb travel moved far less
	// of a slide on a phone (70vw pitch) than on desktop (45vw). Keep it relative to the pitch.
	#tuneDrag() {
		this.config.dragSensitivity = this.dragRatio / (this.pitch || 1)
	}

	// Only the centred slide navigates (Swup picks up the link click);
	// clicking any other slide scrolls it into the centre instead.
	#dragged = false // did the last pointer gesture travel further than dragThreshold
	#samples = [] // recent { x, t } pointer positions for the release velocity

	#bindClicks() {
		let downX = 0
		let pressed = false
		// links are natively draggable, which would swallow the swipe's mouse events
		this.wrapper.addEventListener('dragstart', event => event.preventDefault())
		this.wrapper.addEventListener('pointerdown', event => {
			downX = event.clientX
			pressed = true
			this.#dragged = false
			this.#samples = [{ x: event.clientX, t: event.timeStamp }]
		})
		const { signal } = this.#listeners
		window.addEventListener('pointermove', event => {
			if (!pressed) return
			if (Math.abs(event.clientX - downX) > this.dragThreshold) this.#dragged = true
			this.#samples.push({ x: event.clientX, t: event.timeStamp })
			if (this.#samples.length > 6) this.#samples.shift()
		}, { signal })
		// pointerup fires before the mouseup/touchend Smooothy snaps on, so the target
		// we set here is already whole and its Math.round() leaves it alone
		const release = event => {
			if (!pressed) return
			pressed = false
			this.#flick(event)
		}
		window.addEventListener('pointerup', release, { signal })
		window.addEventListener('pointercancel', release, { signal })
		this.wrapper.addEventListener('click', event => {
			const slide = event.target.closest('[data-record-slide]')
			if (!slide) return

			const index = this.items.indexOf(slide)
			if (!this.#dragged && index === this.currentSlide) return

			event.preventDefault()
			event.stopPropagation()
			if (!this.#dragged) this.goTo(index)
		})
	}

	// Smooothy snaps to the nearest slide on release with no momentum, so a quick short
	// swipe - the natural gesture on a phone - just springs back. Project the release
	// velocity forward so a flick carries, and a fast one always changes slide.
	#flick(event) {
		if (!this.#dragged || this.scrollDirection === 'vertical') return
		const raw = this.dragStartTarget + (event.clientX - this.dragStart) * this.config.dragSensitivity
		const first = this.#samples[0]
		const last = this.#samples[this.#samples.length - 1]
		const elapsed = last.t - first.t
		const velocity = elapsed > 0 ? (last.x - first.x) / elapsed : 0 // px/ms

		let carry = velocity * 1000 * this.config.dragSensitivity * this.flickTime // slides
		carry = Math.max(-this.flickMax, Math.min(this.flickMax, carry))
		let target = Math.round(raw + carry)
		if (target === Math.round(this.dragStartTarget) && Math.abs(velocity) > this.flickMin) {
			target += Math.sign(velocity)
		}
		this.target = target
	}

	// Hover and press feedback on the sleeves, reported per slug so the scene can move the mesh.
	// A press is released on pointer up unless that click navigates - then it holds while the
	// record isolates (the scene clears it when the page swaps).
	#bindPointer(onHover, onPress) {
		if (!onHover && !onPress) return
		this.items.forEach(slide => {
			const link = slide.querySelector('a')
			const slug = slide.dataset.recordSlide
			if (!link) return

			if (onHover) bindPointerLook(link, (on, look) => onHover(slug, on, look))

			link.addEventListener('pointerdown', () => onPress?.(slug, true))
			const release = () => onPress?.(slug, false)
			link.addEventListener('pointercancel', release)
			link.addEventListener('pointerup', () => {
				const navigating = slide === this.items[this.currentSlide] && !this.#dragged
				if (!navigating) release()
			})
		})
	}

	/** Page leaving: take the caption's lines out and keep them out. Resolves when they're gone. */
	leaveCaption() {
		if (!this.caption) return Promise.resolve()
		this.#leaving = true
		extrasOut(this.caption)
		return new Promise(resolve => this.#exit(resolve))
	}

	/** Page arriving: hide the caption's lines now and bring them in once `ready` resolves. */
	async arriveCaption(ready = Promise.resolve()) {
		if (!this.caption) return
		this.#split?.revert()
		this.#split = splitCaption(this.caption)
		gsap.set(this.#split.lines, { yPercent: 100 })
		hideExtras(this.caption)
		this.#phase = 'held'
		await ready
		if (this.#leaving || this.#phase !== 'held') return
		extrasIn(this.caption)
		this.#enter() // brings in whatever was swiped to while held
	}

	#renderCaption(index) {
		if (!this.caption || !this.items[index]) return

		this.#setDots(index) // dots track the live position; only the text is debounced through the transition
		if (this.#leaving) return

		this.#pending = index
		if (this.#phase === 'held') return // arriveCaption() picks up #pending when the page transition ends
		if (this.#shown === null) return this.#setCaption(index)
		if (this.#phase === 'wait') return this.#scheduleEnter() // lines are out: just push the debounce along
		if (index === this.#shown && this.#phase !== 'out') return // already there, or on its way in
		this.#exit()
	}

	/** Write a slide's text straight into the caption, no animation (`animate`: rise the translation mark in like a line). */
	#setCaption(index, { animate = false } = {}) {
		if (!this.caption) return
		const { title = '', subtitle = '', translation = '', translationAlt = '' } = this.items[index].dataset
		this.caption.querySelector('[data-caption-title]').textContent = title
		this.caption.querySelector('[data-caption-subtitle]').textContent = subtitle
		this.#setTranslation(translation, translationAlt, animate)
		this.#shown = this.#pending = index
		this.#setDots(index)
	}

	get #mark() {
		return this.caption.querySelector('[data-caption-translation]')
	}

	// The mark isn't a text line so SplitText doesn't handle it; it's masked by its own wrapper
	// and rides the same out/in motion as the lines when the caption swaps mid-swipe.
	#setTranslation(src, alt, animate) {
		const img = this.#mark
		if (!img) return
		if (img.getAttribute('src') !== src) {
			img.src = src
			img.alt = alt
			img.hidden = !src
		}
		if (!animate || !src) return gsap.set(img, { yPercent: 0 })
		linesIn(img)
	}

	#setDots(index) {
		this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index))
		this.#placeRing(index)
	}

	#placeRing(index, immediate = false) {
		const dot = this.dots[index]
		if (!this.ring || !dot) return
		const target = dot.getBoundingClientRect()
		const ring = this.ring.getBoundingClientRect()
		const nudge = this.ringNudge * parseFloat(getComputedStyle(this.ring).fontSize)
		const delta = target.left + target.width / 2 - (ring.left + ring.width / 2) + nudge
		const x = gsap.getProperty(this.ring, 'x') + delta
		if (immediate) return gsap.set(this.ring, { x })
		gsap.to(this.ring, { x, duration: 0.5, ease: 'power3.out', overwrite: true })
	}

	// `onDone` (page leaving) restarts the exit from wherever the lines are, even mid-exit,
	// so the promise is always tied to a live tween.
	#exit(onDone) {
		if (this.#phase === 'out' && !onDone) return
		if (this.#phase === 'held' || this.#phase === 'wait') {
			// never shown, or already out - just keep it hidden
			this.#enterDelay?.kill()
			gsap.set(this.#split.lines, { yPercent: -100 })
			this.#phase = 'idle'
			return onDone?.()
		}
		this.#phase = 'out'
		this.#tween?.kill()
		this.#split ??= splitCaption(this.caption)
		if (this.#mark) linesOut(this.#mark)
		this.#tween = linesOut(this.#split.lines, {
			onComplete: () => {
				this.#leaving ? this.#rest() : this.#scheduleEnter()
				onDone?.()
			},
			onInterrupt: onDone,
		})
	}

	// lines are out: bring the text in once the swipe stops changing slides (debounced)
	#scheduleEnter() {
		this.#phase = 'wait'
		this.#enterDelay?.kill()
		this.#enterDelay = gsap.delayedCall(this.captionSettle, () => this.#enter())
	}

	#enter() {
		this.#enterDelay = null
		this.#split.revert()
		this.#setCaption(this.#pending, { animate: true })
		this.#phase = 'in'
		this.#split = splitCaption(this.caption)
		this.#tween = linesIn(this.#split.lines, { onComplete: () => this.#rest() })
	}

	#rest() {
		this.#tween = null
		this.#phase = 'idle'
	}
}
